import InstitutionalCta from "./InstitutionalCta";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Languages, LogOut, Menu, User, X } from "lucide-react";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { api, srcCategory, trackEvent } from "../api";
import { Button } from "./ui/button";

// Anonymous 24h session id for aggregate visit counting — never joined to accounts/IPs
const newSid = () =>
  (crypto.randomUUID ? crypto.randomUUID()
    : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
      }));

// Traffic-source classification (srcCategory) lives in ../api so every
// analytics ping can attach the same category enum deterministically.

export default function Layout({ children }) {
  const { lang, toggle, t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // EDU doctrine: the application sells itself — no professional demo CTA on /edu*.
  const isEduRoute = location.pathname === "/edu" || location.pathname.startsWith("/edu/");
  const [offline, setOffline] = useState(!navigator.onLine);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  useEffect(() => {
    try {
      let sid = sessionStorage.getItem("pg_sid");
      const fresh = !sid;
      if (fresh) {
        sid = newSid();
        sessionStorage.setItem("pg_sid", sid);
      }
      const sp = new URLSearchParams(window.location.search);
      const refRaw = sp.get("ref") || "";
      // F3 (2026-10-04): underscores are valid in campaign identifiers.
      const ref = /^[a-z0-9_-]{1,32}$/.test(refRaw) ? refRaw : null;
      if (ref) sessionStorage.setItem("pg_ref", ref);
      // F2 (2026-10-04): privacy-minimizing FIRST-TOUCH attribution — the four
      // approved UTM tokens only, normalized (lowercase/trim/safe charset);
      // the raw query string and referrer URL are never stored.
      const normTok = (v) => { const x = (v || "").trim().toLowerCase(); return /^[a-z0-9_-]{1,32}$/.test(x) ? x : null; };
      if (!sessionStorage.getItem("pg_utm")) {
        const u = {};
        for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_content"]) {
          const v = normTok(sp.get(k)); if (v) u[k] = v;
        }
        if (Object.keys(u).length) sessionStorage.setItem("pg_utm", JSON.stringify(u));
      }
      const utm = JSON.parse(sessionStorage.getItem("pg_utm") || "{}");
      if (fresh) {
        api.post("/analytics/event", { event: "visit", sid, ...(ref ? { ref } : {}), src_cat: srcCategory(ref), path: window.location.pathname, ...utm }).catch(() => {});
        if (ref) sessionStorage.setItem(`pg_vr_${ref}`, "1");
        // Retention signal: localStorage booleans mark a browser that previously
        // converted — a NEW session from it counts as "returning". Aggregate-only:
        // no identity, no history, no fingerprint, flags never leave the browser.
        try {
          const flags = [["pg_ret_r", "returning_after_register"],
                         ["pg_ret_s", "returning_after_save"],
                         ["pg_ret_w", "returning_after_watch"]];
          const active = flags.filter(([k]) => localStorage.getItem(k));
          if (active.length) {
            api.post("/analytics/event", { event: "returning_session", sid }).catch(() => {});
            active.forEach(([, ev]) => api.post("/analytics/event", { event: ev, sid }).catch(() => {}));
          }
        } catch { /* private mode */ }
      } else if (ref && !sessionStorage.getItem(`pg_vr_${ref}`)) {
        // campaign arrival mid-session: count the ref visit once per session per ref
        sessionStorage.setItem(`pg_vr_${ref}`, "1");
        api.post("/analytics/event", { event: "visit", sid, ref, src_cat: srcCategory(ref), path: window.location.pathname, ...utm }).catch(() => {});
      }
    } catch { /* private mode */ }
  }, []);

  // Engagement beacon: qualifies the anonymous session only on real post-load
  // browser behavior — >=5s visible OR first scroll/click/tap/key press.
  // A plain load (or a renderer that never interacts) never qualifies.
  // Once per session; the server additionally requires class=human_likely.
  useEffect(() => {
    let sent = false;
    let timer = null;
    const cleanup = () => {
      if (timer) clearTimeout(timer);
      ["scroll", "pointerdown", "keydown", "touchstart"].forEach((e) =>
        window.removeEventListener(e, fire));
    };
    const fire = (trigger) => {
      if (sent) return;
      sent = true;
      cleanup();
      try {
        if (!sessionStorage.getItem("pg_engaged")) {
          sessionStorage.setItem("pg_engaged", "1");
          // F4 (2026-10-04): the trigger explains qualification downstream
          // (duration_threshold vs interaction_signal) — enum only.
          trackEvent("engaged", { via: trigger });
        }
      } catch { /* private mode */ }
    };
    const armTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { if (!document.hidden) fire("timer"); else armTimer(); }, 5000);
    };
    armTimer();
    ["scroll", "pointerdown", "keydown", "touchstart"].forEach((e) =>
      window.addEventListener(e, () => fire("interaction"), { passive: true, once: true }));
    document.addEventListener("visibilitychange", () => { if (!document.hidden) armTimer(); }, { once: false });
    return cleanup;
  }, []);

  const navItems = [
    { to: "/indeks", label: t("nav.index"), testId: "nav-index-link" },
    { to: "/tracker", label: t("nav.tracker"), testId: "nav-tracker-link" },
    { to: "/konsultacje", label: t("nav.consultations"), testId: "nav-consultations-link" },
    { to: "/pro", label: t("nav.pro"), testId: "nav-pro-link" },
    { to: "/cennik", label: t("nav.pricing"), testId: "nav-pricing-link" },
    { to: "/edu", label: "PlanGmina EDU", testId: "nav-edu-link" },
  ];

  return (
    <div className="flex min-h-screen flex-col">
      {offline && (
        <div data-testid="offline-banner" className="bg-amber-500 px-4 py-2 text-center text-xs font-semibold text-white">
          {t("offline.banner")}
        </div>
      )}
      <header className="sticky top-0 z-40 border-b bg-white/90 backdrop-blur-md no-print">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            <img src="/logo.png" alt="PlanGmina" className="h-7 w-auto sm:h-8" />
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testId}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            {user && (
              <NavLink
                to="/panel"
                data-testid="nav-dashboard-link"
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                {t("nav.dashboard")}
              </NavLink>
            )}
            {user && (
              <NavLink
                to="/workspace"
                data-testid="nav-workspace-link"
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                {t("nav.workspace")}
              </NavLink>
            )}
            {user?.role === "admin" && (
              <NavLink
                to="/admin/analytics"
                data-testid="nav-analytics-link"
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                Analityka
              </NavLink>
            )}
            {user?.role === "admin" && (
              <NavLink
                to="/admin/health"
                data-testid="nav-health-link"
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                Kondycja
              </NavLink>
            )}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="nav-menu-button"
              onClick={() => setMenuOpen((o) => !o)}
              className="rounded-md border px-2.5 py-1.5 text-slate-700 transition-colors hover:bg-slate-50 md:hidden"
              aria-label="Menu"
            >
              {menuOpen ? <X size={16} /> : <Menu size={16} />}
            </button>
            <button
              data-testid="language-toggle-button"
              onClick={toggle}
              className="flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-bold text-slate-700 transition-colors hover:bg-slate-50"
            >
              <Languages size={14} />
              {lang === "pl" ? "EN" : "PL"}
            </button>
            {user ? (
              <div className="flex items-center gap-2">
                <span data-testid="nav-user-email" className="hidden items-center gap-1 text-xs text-slate-500 sm:flex">
                  <User size={14} /> {user.email}
                </span>
                <Button
                  data-testid="nav-logout-button"
                  variant="outline"
                  size="sm"
                  onClick={async () => { await logout(); navigate("/"); }}
                >
                  <LogOut size={14} className="mr-1" /> {t("nav.logout")}
                </Button>
              </div>
            ) : (
              <Button
                data-testid="nav-login-button"
                variant="outline"
                size="sm"
                onClick={() => navigate("/logowanie")}
              >
                {t("nav.login")}
              </Button>
            )}
          </div>
        </div>
        {menuOpen && (
          <nav className="border-t bg-white md:hidden" data-testid="nav-mobile-menu">
            <div className="mx-auto flex max-w-7xl flex-col px-4 py-2 sm:px-6">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`${n.testId}-m`}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2.5 text-sm font-medium ${
                      isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              {user && (
                <NavLink to="/panel" data-testid="nav-dashboard-link-m" onClick={() => setMenuOpen(false)}
                  className={({ isActive }) => `rounded-md px-3 py-2.5 text-sm font-medium ${isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                  {t("nav.dashboard")}
                </NavLink>
              )}
              {user && (
                <NavLink to="/workspace" data-testid="nav-workspace-link-m" onClick={() => setMenuOpen(false)}
                  className={({ isActive }) => `rounded-md px-3 py-2.5 text-sm font-medium ${isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                  {t("nav.workspace")}
                </NavLink>
              )}
              {user?.role === "admin" && (
                <NavLink to="/admin/analytics" data-testid="nav-analytics-link-m" onClick={() => setMenuOpen(false)}
                  className={({ isActive }) => `rounded-md px-3 py-2.5 text-sm font-medium ${isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                  Analityka
                </NavLink>
              )}
              {user?.role === "admin" && (
                <NavLink to="/admin/health" data-testid="nav-health-link-m" onClick={() => setMenuOpen(false)}
                  className={({ isActive }) => `rounded-md px-3 py-2.5 text-sm font-medium ${isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                  Kondycja
                </NavLink>
              )}
            </div>
          </nav>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t bg-slate-900 no-print">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <img src="/logo-transparent.png" alt="PlanGmina" className="h-8 w-auto" />
              </div>
              <p className="mt-2 text-sm text-slate-300">{t("footer.tagline")}</p>
              <p className="mt-1 text-xs font-semibold text-slate-400">{t("hero.motto")}</p>
              <Link to="/aplikacja" data-testid="footer-install-link" className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-slate-300 underline-offset-2 hover:text-white hover:underline">
                {t("nav.install")}
              </Link>
              {!isEduRoute && <InstitutionalCta variant="link" />}
              <Link to="/prywatnosc" data-testid="footer-privacy-link" className="mt-3 block text-xs font-medium text-slate-300 underline-offset-2 hover:text-white hover:underline">
                {t("privacy.link")}
              </Link>
              <Link to="/edu" data-testid="footer-edu-link" className="mt-2 block text-xs font-medium text-slate-300 underline-offset-2 hover:text-white hover:underline">
                Dla uczelni i studentów — PlanGmina EDU
              </Link>
              <span className="mt-1 block text-[11px] text-slate-400">
                <Link to="/edu/regulamin" data-testid="footer-edu-regulamin-link" className="underline-offset-2 hover:text-white hover:underline">Regulamin EDU</Link>
                <span className="mx-1.5 text-slate-600">·</span>
                <Link to="/edu/prywatnosc" data-testid="footer-edu-privacy-link" className="underline-offset-2 hover:text-white hover:underline">EDU — dane osobowe</Link>
                <span className="mx-1.5 text-slate-600">·</span>
                <Link to="/edu/retencja" data-testid="footer-edu-retention-link" className="underline-offset-2 hover:text-white hover:underline">EDU — retencja danych</Link>
              </span>
              <p data-testid="footer-corporate" className="mt-3 text-xs text-slate-400">
                PlanGmina — projekt rozwijany przez Hussar Holdings LLC
                <span className="mx-1.5 text-slate-600">·</span>
                <a data-testid="footer-corporate-email" href="mailto:contact@HussarHoldings.pl" className="font-medium text-slate-300 underline-offset-2 hover:text-white hover:underline">contact@HussarHoldings.pl</a>
                <span className="mx-1.5 text-slate-600">·</span>
                <a data-testid="footer-corporate-site" href="https://hussarholdings.pl" target="_blank" rel="noopener noreferrer" className="font-medium text-slate-300 underline-offset-2 hover:text-white hover:underline">HussarHoldings.pl</a>
              </p>
            </div>
            <p data-testid="footer-disclaimer" className="max-w-xl text-xs leading-relaxed text-slate-300">
              {t("disclaimer.text")}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
